import React, { useEffect, useMemo, useState } from 'react';
import { Row, Col, Card, Form, Button, Table } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import API from '../services/api';
import ApprovalChip from './ApprovalChip';
import Pager from './Pager';
import { useAuth } from '../context/AuthContext';
import ClientPricingModal from './ClientPricingModal';
import ClientStatsModal from './ClientStatsModal';
import ClientQuotesModal from './ClientQuotesModal';
import ClientPortalModal from './ClientPortalModal';
import Loading from './Loading';

const emptyForm = {
  name: '', tipo: 'juridica', rnc: '', cedula: '', email: '', phone: '', address: '',
  regimenEspecial: false, esGubernamental: false,
};

// Groups digits with dashes as the user types, e.g. formatDigits('131283721', [3, 5, 1]) -> '131-28372-1'.
const formatDigits = (raw, groups) => {
  const maxLength = groups.reduce((sum, len) => sum + len, 0);
  const digits = raw.replace(/\D/g, '').slice(0, maxLength);
  const parts = [];
  let idx = 0;
  for (const len of groups) {
    if (idx >= digits.length) break;
    parts.push(digits.slice(idx, idx + len));
    idx += len;
  }
  return parts.join('-');
};

const formatRnc = (raw) => formatDigits(raw, [3, 5, 1]);
const formatCedula = (raw) => formatDigits(raw, [3, 7, 1]);
const formatPhone = (raw) => formatDigits(raw, [3, 3, 4]);

const money = (n) => new Intl.NumberFormat('es-DO', { style: 'currency', currency: 'DOP' }).format(n || 0);

const ClientsPage = () => {
  const [clients, setClients] = useState([]);
  const [loaded, setLoaded] = useState(false); // the first load of the list has come back
  const [stats, setStats] = useState(null);
  const [search, setSearch] = useState('');
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [error, setError] = useState('');
  const [pricingClient, setPricingClient] = useState(null);
  const [statsClient, setStatsClient] = useState(null);
  const [quotesClient, setQuotesClient] = useState(null);
  const [portalClient, setPortalClient] = useState(null);
  const [portalCounts, setPortalCounts] = useState({});
  const [rncLookup, setRncLookup] = useState({ state: 'idle', result: null });
  const [rncRefreshMessage, setRncRefreshMessage] = useState('');
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';

  const loadClients = () => {
    API.get('/clients').then(res => setClients(res.data)).catch(err => console.error('Error loading clients:', err)).finally(() => setLoaded(true));
  };

  const loadStats = () => {
    API.get('/clients/stats').then(res => setStats(res.data)).catch(err => console.error('Error loading client stats:', err));
  };

  // How many people of each client can sign in to the customer portal (admins manage it).
  const loadPortalCounts = () => {
    if (isAdmin) API.get('/portal-access/summary').then(res => setPortalCounts(res.data)).catch(() => {});
  };

  useEffect(() => {
    loadClients();
    loadStats();
    loadPortalCounts();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleFormattedChange = (formatter) => (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: formatter(value) }));
  };

  const handleTipoChange = (value) => {
    setForm(prev => ({
      ...prev,
      tipo: value,
      rnc: value === 'fisica' ? '' : prev.rnc,
      cedula: value === 'juridica' ? '' : prev.cedula,
    }));
    setRncLookup({ state: 'idle', result: null });
  };

  const handleRncBlur = async () => {
    const digits = form.rnc.replace(/\D/g, '');
    if (form.tipo !== 'juridica' || digits.length < 9) {
      setRncLookup({ state: 'idle', result: null });
      return;
    }
    setRncLookup({ state: 'loading', result: null });
    try {
      const { data } = await API.get(`/rnc-lookup/${digits}`);
      setRncLookup({ state: 'found', result: data });
      setForm(prev => (prev.name ? prev : { ...prev, name: data.name }));
    } catch (err) {
      if (err.response?.status === 404) {
        setRncLookup({ state: 'not_found', result: null });
      } else {
        console.error('Error looking up RNC:', err);
        setRncLookup({ state: 'idle', result: null });
      }
    }
  };

  const handleRefreshRncRegistry = async () => {
    setRncRefreshMessage('Iniciando actualización...');
    try {
      await API.post('/rnc-lookup/refresh');
      setRncRefreshMessage('Actualización del padrón RNC en curso — puede tardar unos minutos.');
    } catch (err) {
      setRncRefreshMessage(err.response?.data?.message || 'Error al iniciar la actualización.');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    try {
      if (editingId) {
        await API.put(`/clients/${editingId}`, form);
      } else {
        await API.post('/clients', form);
      }
      setForm(emptyForm);
      setEditingId(null);
      setRncLookup({ state: 'idle', result: null });
      loadClients();
      loadStats();
    } catch (err) {
      setError(err.response?.data?.message || 'Error al guardar el cliente.');
    }
  };

  const handleEdit = (client) => {
    setError('');
    setEditingId(client._id);
    setForm({
      name: client.name || '',
      tipo: client.tipo || 'fisica',
      rnc: client.rnc || '',
      cedula: client.cedula || '',
      email: client.email || '',
      phone: client.phone || '',
      address: client.address || '',
      regimenEspecial: !!client.regimenEspecial,
      esGubernamental: !!client.esGubernamental,
    });
    setRncLookup({ state: 'idle', result: null });
  };

  const handleCancelEdit = () => {
    setError('');
    setEditingId(null);
    setForm(emptyForm);
    setRncLookup({ state: 'idle', result: null });
  };

  const handleDelete = async (id) => {
    try {
      const res = await API.delete(`/clients/${id}`);
      // 202: a sales user asked an admin to delete it — the client is still here until they approve.
      if (res.status !== 202 && id === editingId) handleCancelEdit();
      loadClients();
      loadStats();
    } catch (err) {
      console.error('Error deleting client:', err);
    }
  };

  const filteredClients = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return clients;
    return clients.filter(c =>
      c.name?.toLowerCase().includes(term) ||
      c.rnc?.toLowerCase().includes(term) ||
      c.cedula?.toLowerCase().includes(term) ||
      c.email?.toLowerCase().includes(term) ||
      c.phone?.toLowerCase().includes(term)
    );
  }, [clients, search]);

  // 10 per page (the app's list pattern); a new search starts at page 1. Ported from ventas 6e972ed.
  const PAGE_SIZE = 10;
  const [page, setPage] = useState(1);
  useEffect(() => { setPage(1); }, [search]);
  const pages = Math.max(1, Math.ceil(filteredClients.length / PAGE_SIZE));
  const currentPage = Math.min(page, pages);
  const pagedClients = filteredClients.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  // "Mejor cliente" is admin-only, so sales have 3 stat tiles: share the row evenly instead of leaving a gap.
  const statCols = user?.role === 'admin' ? 3 : 4;

  // Like Manifold Manager: nothing but the loading indicator until the page's data has come back.
  if (!loaded) return <Loading page label="Cargando clientes..." />;

  return (
    <div className="container-fluid p-4">
      <h2 className="app-title mb-4" style={{ textAlign: 'left' }}>Clientes</h2>

      <Row className="mb-3">
        <Col md={statCols}>
          <Card className="shadow-sm p-3 dashboard-card">
            <div className="text-muted small">Total Clientes</div>
            <h2 className="mt-2 mb-0">{stats?.totalClients ?? '—'}</h2>
          </Card>
        </Col>
        <Col md={statCols}>
          <Card className="shadow-sm p-3 dashboard-card">
            <div className="text-muted small">Nuevos este mes</div>
            <h2 className="mt-2 mb-0" style={{ color: 'var(--mm-green)' }}>{stats?.newThisMonth ?? '—'}</h2>
          </Card>
        </Col>
        <Col md={statCols}>
          <Card className="shadow-sm p-3 dashboard-card">
            <div className="text-muted small">Cuentas por cobrar</div>
            <h2 className="mt-2 mb-0" style={{ color: 'var(--mm-yellow)' }}>{money(stats?.outstandingTotal)}</h2>
          </Card>
        </Col>
        {stats && 'topClient' in stats && (
        <Col md={statCols}>
          <Card className="shadow-sm p-3 dashboard-card">
            <div className="text-muted small">Mejor cliente</div>
            {stats?.topClient ? (
              <>
                <h5 className="mt-2 mb-1">{stats.topClient.name}</h5>
                <div style={{ color: 'var(--mm-cyan)', fontWeight: 600 }}>{money(stats.topClient.total)}</div>
              </>
            ) : (
              <div className="text-muted mt-2">Sin datos todavía.</div>
            )}
          </Card>
        </Col>
        )}
      </Row>

      <Row>
        <Col md={4} className="mb-3">
          <Card className="shadow-sm p-3 h-100">
            <div className="d-flex justify-content-between align-items-center mb-2">
              <Card.Title className="mb-0">{editingId ? 'Editar Cliente' : 'Nuevo Cliente'}</Card.Title>
              {user?.role === 'admin' && (
                <Button size="sm" variant="outline-secondary" onClick={handleRefreshRncRegistry}>
                  Actualizar padrón RNC
                </Button>
              )}
            </div>
            {rncRefreshMessage && <div className="text-muted small mb-2">{rncRefreshMessage}</div>}
            {error && <div className="alert alert-danger">{error}</div>}
            <Form onSubmit={handleSubmit}>
              <Form.Group className="mb-2">
                <Form.Label>Nombre / Razón Social</Form.Label>
                <Form.Control name="name" value={form.name} onChange={handleChange} required />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label className="d-block">Tipo</Form.Label>
                <div className="type-switch">
                  <div
                    className="type-switch-thumb"
                    style={{ transform: form.tipo === 'juridica' ? 'translateX(100%)' : 'translateX(0)' }}
                  />
                  <button
                    type="button"
                    className={`type-switch-option ${form.tipo === 'fisica' ? 'active' : ''}`}
                    onClick={() => handleTipoChange('fisica')}
                  >
                    Persona
                  </button>
                  <button
                    type="button"
                    className={`type-switch-option ${form.tipo === 'juridica' ? 'active' : ''}`}
                    onClick={() => handleTipoChange('juridica')}
                  >
                    Empresa
                  </button>
                </div>
              </Form.Group>
              <Row>
                <Col xl={6}>
              {form.tipo === 'juridica' && (
                <Form.Group className="mb-2">
                  <Form.Label>RNC</Form.Label>
                  <Form.Control
                    name="rnc"
                    value={form.rnc}
                    onChange={e => { handleFormattedChange(formatRnc)(e); setRncLookup({ state: 'idle', result: null }); }}
                    onBlur={handleRncBlur}
                    required
                    placeholder="000-00000-0"
                  />
                  {rncLookup.state === 'loading' && (
                    <Form.Text className="text-muted">Buscando en el padrón DGII...</Form.Text>
                  )}
                  {rncLookup.state === 'found' && (
                    <Form.Text style={{ color: rncLookup.result.estado === 'ACTIVO' ? 'var(--mm-green)' : 'var(--mm-yellow)' }}>
                      {rncLookup.result.estado === 'ACTIVO' ? '✓' : '⚠'} {rncLookup.result.name} — Estado: {rncLookup.result.estado || 'desconocido'}
                    </Form.Text>
                  )}
                  {rncLookup.state === 'not_found' && (
                    <Form.Text className="text-muted">No encontrado en el padrón DGII.</Form.Text>
                  )}
                </Form.Group>
              )}
              {form.tipo === 'fisica' && (
                <Form.Group className="mb-2">
                  <Form.Label>Cédula</Form.Label>
                  <Form.Control
                    name="cedula"
                    value={form.cedula}
                    onChange={handleFormattedChange(formatCedula)}
                    placeholder="000-0000000-0"
                  />
                </Form.Group>
              )}
                </Col>
                <Col xl={6}>
              <Form.Group className="mb-2">
                <Form.Label>Teléfono</Form.Label>
                <Form.Control
                  name="phone"
                  value={form.phone}
                  onChange={handleFormattedChange(formatPhone)}
                  required
                  placeholder="000-000-0000"
                />
              </Form.Group>
                </Col>
              </Row>
              <Form.Group className="mb-2">
                <Form.Label>Email</Form.Label>
                <Form.Control type="email" name="email" value={form.email} onChange={handleChange} />
              </Form.Group>
              <Form.Group className="mb-2">
                <Form.Label>Dirección</Form.Label>
                <Form.Control as="textarea" rows={2} name="address" value={form.address} onChange={handleChange} />
              </Form.Group>
              <Form.Check className="mb-2" label="Régimen especial (zona franca, etc.)" name="regimenEspecial" checked={form.regimenEspecial} onChange={handleChange} />
              <Form.Check className="mb-3" label="Entidad gubernamental" name="esGubernamental" checked={form.esGubernamental} onChange={handleChange} />
              <div className="d-flex gap-2">
                {editingId && (
                  <Button type="button" variant="outline-secondary" className="w-100" onClick={handleCancelEdit}>Cancelar</Button>
                )}
                <Button type="submit" className="w-100">{editingId ? 'Guardar Cambios' : 'Guardar Cliente'}</Button>
              </div>
            </Form>
          </Card>
        </Col>
        <Col md={8} className="mb-3">
          <Card className="shadow-sm p-3 h-100">
            <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
              <Card.Title className="mb-0">Listado de Clientes <span className="text-muted" style={{ fontSize: '0.85rem', fontWeight: 400 }}>· {filteredClients.length}</span></Card.Title>
              <Form.Control
                style={{ maxWidth: 380 }}
                placeholder="Buscar por nombre, RNC, email o teléfono..."
                value={search}
                onChange={e => setSearch(e.target.value)}
              />
            </div>
            <Table hover responsive className="mb-0">
              <thead>
                <tr>
                  <th>Nombre</th>
                  <th>Tipo</th>
                  <th>RNC/Cédula</th>
                  <th>Contacto</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {pagedClients.map(c => (
                  <tr key={c._id}>
                    <td>
                      {c.name} <ApprovalChip entityId={c._id} className="ms-1" />
                      {isAdmin && portalCounts[c._id] > 0 && (
                        <span className="badge rounded-pill ms-1" style={{ background: 'var(--mm-cyan-dim)', color: 'var(--mm-cyan)', fontWeight: 600 }}
                          title={`${portalCounts[c._id]} persona(s) con acceso de cliente`}><i className="bi bi-phone" /> Acceso</span>
                      )}
                    </td>
                    <td>{c.tipo === 'juridica' ? 'Empresa' : 'Persona'}</td>
                    <td>{c.rnc || c.cedula || '—'}</td>
                    <td>{c.email || c.phone || '—'}</td>
                    <td className="text-end" style={{ minWidth: 100 }}>
                      <div className="row-actions">
                        <Button size="sm" variant="outline-info" title="Historial" onClick={() => setStatsClient(c)}><i className="bi bi-clock-history" /></Button>
                        <Button size="sm" variant="outline-primary" title="Facturas" onClick={() => navigate(`/invoices?client=${c._id}&clientName=${encodeURIComponent(c.name)}`)}><i className="bi bi-receipt" /></Button>
                        <Button size="sm" variant="outline-warning" title="Cotizaciones" onClick={() => setQuotesClient(c)}><i className="bi bi-file-earmark-text" /></Button>
                        <Button size="sm" variant="outline-secondary" title="Precios" onClick={() => setPricingClient(c)}><i className="bi bi-tag" /></Button>
                        {isAdmin && <Button size="sm" variant="outline-info" title="Acceso de cliente (app de clientes)" onClick={() => setPortalClient(c)}><i className="bi bi-phone" /></Button>}
                        <Button size="sm" variant="outline-secondary" title="Editar" onClick={() => handleEdit(c)}><i className="bi bi-pencil" /></Button>
                        <Button size="sm" variant="outline-danger" title="Eliminar" onClick={() => handleDelete(c._id)}><i className="bi bi-trash" /></Button>
                      </div>
                    </td>
                  </tr>
                ))}
                {filteredClients.length === 0 && (
                  <tr><td colSpan={5} className="text-muted text-center">
                    {!loaded ? <Loading label="Cargando clientes..." /> : search ? `Ningún cliente coincide con "${search}".` : 'Sin clientes todavía.'}
                  </td></tr>
                )}
              </tbody>
            </Table>
            {pages > 1 && (
              <div className="d-flex justify-content-between align-items-center mt-3 flex-wrap gap-2">
                <span className="text-muted small">Página {currentPage} de {pages} — {filteredClients.length} cliente{filteredClients.length === 1 ? '' : 's'}</span>
                <Pager page={currentPage} pages={pages} onChange={setPage} />
              </div>
            )}
          </Card>
        </Col>
      </Row>

      {pricingClient && (
        <ClientPricingModal client={pricingClient} onClose={() => setPricingClient(null)} />
      )}
      {statsClient && (
        <ClientStatsModal client={statsClient} onClose={() => setStatsClient(null)} />
      )}
      {portalClient && (
        <ClientPortalModal client={portalClient} onClose={() => setPortalClient(null)} onChanged={loadPortalCounts} />
      )}
      {quotesClient && (
        <ClientQuotesModal client={quotesClient} onClose={() => setQuotesClient(null)} />
      )}
    </div>
  );
};

export default ClientsPage;
